import type { InstrumentFormState } from '@/config/instruments/formUtils';
import { convertInstrumentToFormState, createEmptyFormState } from '@/config/instruments/formUtils';
import type { InstrumentPublic } from '@/types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type FormErrors = Partial<Record<keyof InstrumentFormState, string>>;

export interface UseInstrumentFormReturn {
  form: InstrumentFormState;
  errors: FormErrors;
  setForm: (key: keyof InstrumentFormState, value: InstrumentFormState[keyof InstrumentFormState]) => void;
  validate: () => boolean;
  resetForm: () => void;
}

interface UseInstrumentFormOptions {
  instrument?: InstrumentPublic;
}

export const useInstrumentForm = (options: UseInstrumentFormOptions): UseInstrumentFormReturn => {
  const { t } = useTranslation('instruments');
  const { instrument } = options;

  const [form, setFormState] = useState<InstrumentFormState>(() =>
    instrument ? convertInstrumentToFormState(instrument) : createEmptyFormState(),
  );
  const [errors, setErrors] = useState<FormErrors>({});

  // Reset when instrument changes (edit mode switch).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setFormState(instrument ? convertInstrumentToFormState(instrument) : createEmptyFormState());
    setErrors({});
  }, [instrument]);

  const resetForm = useCallback(() => {
    setFormState(instrument ? convertInstrumentToFormState(instrument) : createEmptyFormState());
    setErrors({});
  }, [instrument]);

  const setForm = useCallback(<K extends keyof InstrumentFormState>(key: K, value: InstrumentFormState[K]) => {
    setFormState((prev: InstrumentFormState) => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }, []);

  const validate = useCallback((): boolean => {
    const next: FormErrors = {};

    if (!form.name.trim()) {
      next.name = t('validation_name_required');
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form, t]);

  return {
    form,
    errors,
    setForm,
    validate,
    resetForm,
  };
};
